/**
 * Org Param Group: Controle de estoque ('stock')
 *
 * Company-wide stock control settings. Consumed by stock-related features via
 * `useOrgParams('stock')`. See spec 0012 §"Controle de estoque" and requests 0006–0009.
 *
 * Parameters:
 *   Policy (orgEditable: false, edited in organization-manager):
 *     stockControlEnabled, allowNegativeStock, reservationPolicy, lotControl,
 *     multiWarehouse, expirationTracking
 *
 *   Operational (orgEditable: true, edited in organization-configs module):
 *     defaultStockLocationId — always available when the group is enabled (0094)
 */
const parameters = [
    // ─── Policy params (superadmin / organization-manager) ─────────────────────
    {
        key: 'stockControlEnabled',
        label: 'Controle de estoque ativo',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
    },
    {
        key: 'allowNegativeStock',
        label: 'Permitir estoque negativo',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
    },
    {
        key: 'reservationPolicy',
        label: 'Política de reserva',
        type: 'enum',
        scope: 'module',
        options: ['none', 'on-order', 'on-confirmation'],
        default: 'none',
        required: false,
    },
    {
        key: 'lotControl',
        label: 'Controle de lote',
        type: 'enum',
        scope: 'module',
        options: ['off', 'optional', 'mandatory'],
        default: 'off',
        required: false,
    },
    {
        key: 'multiWarehouse',
        label: 'Múltiplos depósitos',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
    },
    {
        key: 'expirationTracking',
        label: 'Rastrear validade',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
    },
    // ─── Operational params (org-admin / organization-configs module) ──────────
    {
        key: 'defaultStockLocationId',
        label: 'Local de estoque padrão',
        type: 'reference',
        scope: 'module',
        referenceEntity: 'StockLocation',
        displayField: 'description',
        default: null,
        required: false,
        orgEditable: true,
    },
    {
        key: 'stockDirectedTasks',
        label: 'Fila dirigida de tarefas (WMS)',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
        orgEditable: true,
    },
    {
        key: 'receivingVolumePolicy',
        label: 'Divisão padrão em volumes (recebimento)',
        type: 'enum',
        scope: 'module',
        options: ['one-per-unit', 'single', 'manual'],
        default: 'single',
        required: false,
        orgEditable: true,
    },
    {
        // 0346 — temporary operator aid; text is frozen at materialize (invoice only).
        key: 'volumeSequenceInNotes',
        label: 'Gravar a sequência do item na observação do volume (Volume n/N)',
        type: 'boolean',
        scope: 'module',
        default: false,
        required: false,
        orgEditable: true,
        description: 'Temporário. O texto é gravado quando o volume é gerado e não é recalculado depois.',
    },
    {
        key: 'quarantineStockLocationId',
        label: 'Local de quarentena (avaria no recebimento)',
        type: 'reference',
        scope: 'module',
        referenceEntity: 'StockLocation',
        displayField: 'description',
        default: null,
        required: false,
        orgEditable: true,
    },
    {
        // 0206 §1.1 — free text, not a `list` type (there isn't one). Comma/semicolon separated
        // CFOPs; parsed and compared with `parseThirdPartyCfops`/`isThirdPartyCfop`.
        key: 'thirdPartyEntryCfops',
        label: 'CFOPs de entrada de terceiros',
        type: 'string',
        scope: 'module',
        default: '',
        required: false,
        orgEditable: true,
        availableWhen: (v) => v['stockControlEnabled'] === true,
    },
    {
        // 0206 §1.2 — mirrors quarantineStockLocationId. Obrigatório: sem ele, entrada de terceiro
        // não tem para onde ir (0203 recusa dono em local que não seja kind: 'third-party').
        key: 'thirdPartyStockLocationId',
        label: 'Local de estoque de terceiros',
        type: 'reference',
        scope: 'module',
        referenceEntity: 'StockLocation',
        displayField: 'description',
        default: null,
        required: false,
        orgEditable: true,
    },
];
export const stockOrgParamGroup = {
    key: 'stock',
    label: 'Controle de estoque',
    version: 7,
    parameters,
    rules: {
        evaluate(values) {
            const enabled = values['stockControlEnabled'] === true;
            if (enabled) {
                return { disabled: {}, reasons: {} };
            }
            const dependents = [
                'allowNegativeStock',
                'reservationPolicy',
                'lotControl',
                'multiWarehouse',
                'expirationTracking',
                'defaultStockLocationId',
                'stockDirectedTasks',
                'receivingVolumePolicy',
                'volumeSequenceInNotes',
                'quarantineStockLocationId',
                'thirdPartyEntryCfops',
                'thirdPartyStockLocationId',
            ];
            const disabled = {};
            const reasons = {};
            for (const key of dependents) {
                disabled[key] = true;
                reasons[key] = 'Requer controle de estoque ativo';
            }
            return { disabled, reasons };
        },
        validate(_values) {
            return [];
        },
        /**
         * Resets all stock-dependent params to their manifest defaults whenever
         * stockControlEnabled is false. Prevents stale non-default values from being
         * persisted in the delta for fields that are semantically disabled.
         */
        coerce(values) {
            if (values['stockControlEnabled'] === true)
                return {};
            return {
                allowNegativeStock: false,
                reservationPolicy: 'none',
                lotControl: 'off',
                multiWarehouse: false,
                expirationTracking: false,
                defaultStockLocationId: null,
                stockDirectedTasks: false,
                receivingVolumePolicy: 'single',
                volumeSequenceInNotes: false,
                quarantineStockLocationId: null,
                thirdPartyEntryCfops: '',
                thirdPartyStockLocationId: null,
            };
        },
    },
};
//# sourceMappingURL=stock.orgParamGroup.js.map